import { useEffect, useState } from "react";
import { Calendar, Clock, Plus, Power, Trash2, Play, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const PRESETS: { label: string; expr: string }[] = [
  { label: "A cada minuto", expr: "* * * * *" },
  { label: "A cada 15 min", expr: "*/15 * * * *" },
  { label: "A cada hora", expr: "0 * * * *" },
  { label: "Diário 09:00", expr: "0 9 * * *" },
  { label: "Seg 08:30", expr: "30 8 * * 1" },
  { label: "Sex 17:00", expr: "0 17 * * 5" },
];

type Schedule = {
  id: string;
  name: string;
  prompt: string;
  cron_expr: string;
  enabled: boolean;
  models: string[];
  last_run_at: string | null;
  cron_job_id: number | null;
};

export function Scheduler() {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [name, setName] = useState("");
  const [cron, setCron] = useState(PRESETS[2].expr);
  const [prompt, setPrompt] = useState("Faça um resumo das últimas notícias de IA e me envie.");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    if (!user) return;
    const { data } = await supabase.from("schedules").select("*").order("created_at", { ascending: false });
    setSchedules((data as any) ?? []);
  };

  useEffect(() => {
    reload();
    if (!user) return;
    const ch = supabase
      .channel("schedules_rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "schedules", filter: `user_id=eq.${user.id}` }, reload)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const create = async () => {
    if (!user) return toast.error("Faça login");
    if (!name.trim() || !prompt.trim()) return toast.error("Nome e prompt obrigatórios");
    setBusy(true);
    const { error } = await supabase.from("schedules").insert({
      user_id: user.id,
      name: name.trim(),
      prompt: prompt.trim(),
      cron_expr: cron,
      enabled: true,
      models: ["gemini-3-flash"],
    });
    setBusy(false);
    if (error) toast.error(error.message);
    else { setName(""); setPrompt(""); toast.success("Schedule criado"); }
  };

  const toggle = async (s: Schedule) => {
    await supabase.from("schedules").update({ enabled: !s.enabled }).eq("id", s.id);
  };

  const remove = async (id: string) => {
    await supabase.from("schedules").delete().eq("id", id);
  };

  const runNow = async (s: Schedule) => {
    toast.loading(`Executando "${s.name}"...`, { id: s.id });
    const { data, error } = await supabase.functions.invoke("execute-schedule", {
      body: { schedule_id: s.id },
    });
    if (error) toast.error(error.message, { id: s.id });
    else toast.success(`OK · ${(data as any)?.content_preview?.slice(0, 80) ?? "executado"}`, { id: s.id });
  };

  return (
    <div className="h-full overflow-y-auto p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <Calendar className="w-6 h-6 text-primary" /> Scheduler
        </h1>
        <p className="text-xs text-muted-foreground mt-1">Schedules persistidos · execução real via Edge Function · pronto para pg_cron</p>
      </div>

      <div className="glass rounded-xl p-4 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Novo Schedule</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome (ex: Daily Brief)" className="h-9" />
          <select
            value={cron}
            onChange={(e) => setCron(e.target.value)}
            className="h-9 px-2 rounded bg-surface-2 border border-border text-sm"
          >
            {PRESETS.map((p) => <option key={p.expr} value={p.expr}>{p.label} · {p.expr}</option>)}
          </select>
          <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Prompt para o agente" className="h-9" />
        </div>
        <Button size="sm" disabled={busy} className="bg-gradient-neural text-primary-foreground border-0 gap-1.5" onClick={create}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Criar
        </Button>
      </div>

      <div className="space-y-2">
        {schedules.map((s) => (
          <div key={s.id} className="glass rounded-lg p-3 flex items-center gap-3">
            <span className={`status-dot ${s.enabled ? "text-success" : "text-muted-foreground"}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {s.name} <span className="text-muted-foreground font-mono text-xs">· {s.cron_expr}</span>
              </div>
              <div className="text-xs text-muted-foreground truncate">{s.prompt}</div>
            </div>
            <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" /> {s.last_run_at ? new Date(s.last_run_at).toLocaleTimeString() : "—"}
            </div>
            <button onClick={() => runNow(s)} className="p-1.5 rounded hover:bg-primary/10 text-primary" title="Run now">
              <Play className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => toggle(s)} className="p-1.5 rounded hover:bg-surface-3" title="Toggle">
              <Power className={`w-3.5 h-3.5 ${s.enabled ? "text-success" : "text-muted-foreground"}`} />
            </button>
            <button onClick={() => remove(s.id)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {schedules.length === 0 && (
          <div className="text-center text-xs text-muted-foreground p-8 border border-dashed border-border rounded-lg">
            Nenhum schedule. Crie um acima.
          </div>
        )}
      </div>
    </div>
  );
}
